import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, FileText } from "lucide-react";

import { api, background } from "../../api/client";
import type { ChiefNotice, Exercise } from "../../api/types";
import { Banner, Callout } from "../../components/Callout";
import { Presence } from "../../components/presence";
import { Button } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { useWriteBlock } from "../../sharing/access";
import { useUi } from "../../state/ui";
import { chiefNoticeText, isTransmitted, useReceivedFocus } from "./chiefNotice";

/** Ordres transmis prévenus (13/10/2026) : avis du dossier transmis ouvert, relu à chaque ouverture (le serveur relit
 *  d'abord l'ordre du chef s'il a changé, retouche à la main comprise). */
export function useChiefNotice(exercise: Exercise | null | undefined): ChiefNotice | null {
  const id = exercise?.id ?? null;
  const query = useQuery({
    queryKey: ["chain-notice", id],
    queryFn: () => background(() => api.chainNotice(id as string)),
    enabled: Boolean(id && isTransmitted(exercise)),
    staleTime: 0,
  });
  return isTransmitted(exercise) ? (query.data?.notice ?? null) : null;
}

/** Actions de l'avis : « Voir l'ordre reçu » (action attendue), « Reporter les changements » (le report des Ordres
 *  reçus, case par case), « Ignorer ce changement ». */
function useNoticeActions(exercise: Exercise | null | undefined, notice: ChiefNotice | null) {
  const queryClient = useQueryClient();
  const setView = useUi((s) => s.setView);
  const notify = useUi((s) => s.notify);
  const setFocus = useReceivedFocus((s) => s.setFocus);
  const open = (mode: "view" | "apply") => {
    if (!exercise || !notice) return;
    setFocus({ exerciseId: exercise.id, orderId: notice.received, mode });
    void queryClient.invalidateQueries({ queryKey: ["received", exercise.id] });
    setView("received");
  };
  const ignore = useMutation({
    mutationFn: () => api.ignoreChainNotice(exercise!.id),
    onSuccess: () => {
      queryClient.setQueryData(["chain-notice", exercise!.id], { notice: null });
      void queryClient.invalidateQueries({ queryKey: ["received", exercise!.id] });
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      notify({ text: t("Changement ignoré : rien n'a été reporté dans les Données initiales."), tone: "info" });
    },
    onError: (e: Error) => notify({ text: t("Opération impossible"), detail: e.message, tone: "error" }),
  });
  return { open, ignore };
}

function NoticeActions({ exercise, notice, compact }: { exercise: Exercise; notice: ChiefNotice; compact?: boolean }) {
  const writeBlock = useWriteBlock();
  const { open, ignore } = useNoticeActions(exercise, notice);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button size="sm" highlight onClick={() => open("view")}>
        <FileText size={14} /> {t("Voir l'ordre reçu")}
      </Button>
      <Button size="sm" variant="secondary" onClick={() => open("apply")} {...writeBlock}>
        <ArrowRightLeft size={14} /> {t("Reporter les changements")}
      </Button>
      <Button size={compact ? "sm" : undefined} variant="link" disabled={ignore.isPending} onClick={() => ignore.mutate()} {...writeBlock}>
        {t("Ignorer ce changement")}
      </Button>
    </div>
  );
}

/** Bandeau des vues plein cadre (ordre, ordres reçus) : sous l'en-tête, en fondu. */
export function ChiefOrderBanner({ exercise }: { exercise: Exercise | null | undefined }) {
  const notice = useChiefNotice(exercise);
  const show = Boolean(exercise && notice);
  return (
    <Presence show={show}>
      {exercise && notice && (
        <Banner tone="info" actions={<NoticeActions exercise={exercise} notice={notice} compact />}>
          {chiefNoticeText(notice)}
        </Banner>
      )}
    </Presence>
  );
}

/** Encadré des Données initiales (vue ouverte avec le dossier) : même avis, mêmes actions. */
export function ChiefOrderCallout({ exercise }: { exercise: Exercise | null | undefined }) {
  const notice = useChiefNotice(exercise);
  const show = Boolean(exercise && notice);
  return (
    <Presence show={show}>
      {exercise && notice && (
        <Callout tone="info">
          <p>{chiefNoticeText(notice)}</p>
          <p className="mt-0.5 text-ink-soft">{t("Rien n'est reporté d'office : relisez l'ordre reçu, puis reportez ce qui convient.")}</p>
          <div className="mt-2">
            <NoticeActions exercise={exercise} notice={notice} compact />
          </div>
        </Callout>
      )}
    </Presence>
  );
}
